import { MailOutlined, StopOutlined } from "@ant-design/icons";
import {
  Button,
  Form,
  Input,
  message,
  Modal,
  Popconfirm,
  Select,
  Switch,
  Tabs,
} from "antd";
import React, { lazy, Suspense, useEffect, useState } from "react";
import { $t } from "~/i18n.js";

import { AnnuleerButton } from "~/components/buttons/AnnuleerButton.jsx";
import { SaveButton } from "~/components/buttons/SaveButton.jsx";
import { FileInput, FileValidator } from "~/components/fileInput/fileInput.jsx";
import { CompetencySelector } from "~/components/form/CompetencySelector.jsx";
import { GroupFormSelect } from "~/components/form/GroupFormSelect.jsx";
import { HelpBubble } from "~/components/help/helpBubble.jsx";
import { Loading } from "~/components/loader/Loading.jsx";
import { validateEmail } from "~/lib/helpers.js";
import { _parseDate } from "~/lib/localize.js";
import {
  sendTestCampaign,
  uploadCampaignImage,
} from "~/redux/admin/admin.actions.js";
import { EmailCampaignStatus } from "./EmailPage.jsx";
import { EmailStats } from "./EmailStats.jsx";

const RichTextEditor = lazy(
  () => import("~/components/markdown/RichTextEditor.jsx"),
);

const SAVE = "save";
const DELETE = "delete";
const TEXT = "text";
const HTML = "html";
const MAX_LENGTH = { [TEXT]: 5000, [HTML]: 50000 };
const IMAGE_TYPES = ["image/jpeg", "image/png", "image/gif"];
const MAX_IMAGE_BYTES = 8 * 1024 * 1024;
const MAX_IMAGES = 10;

const EDITOR_PLUGINS = "link image lists autolink table code";
const EDITOR_TOOLBAR =
  "undo redo | blocks | bold italic underline | forecolor | alignleft aligncenter alignright | bullist numlist | link image table | removeformat code";
const EDITOR_OVERRIDES = {
  relative_urls: false,
  convert_urls: false,
  link_default_protocol: "https",
};

const escapeHtml = (text) =>
  text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const textToHtml = (text) =>
  (text || "")
    .split("\n")
    .map((line) => `<p>${escapeHtml(line) || "<br>"}</p>`)
    .join("");

const htmlToText = (html) => {
  const doc = new DOMParser().parseFromString(
    (html || "").replace(/<br\s*\/?>|<\/(p|div|h[1-4]|li)>/gi, "\n"),
    "text/html",
  );
  return doc.body.textContent.replace(/\n{3,}/g, "\n\n").trim();
};

export const EmailForm = ({
  visible,
  groups,
  activeEmail,
  closeModal,
  saveEmail,
  group_name_lookup,
}) => {
  if (!activeEmail) return null;

  const [loadingAction, setLoading] = useState("");

  const [form] = Form.useForm();
  const content = Form.useWatch("content", form);
  const contentFormat =
    Form.useWatch("content_format", form) ??
    (activeEmail?.content_format || HTML);
  const [testing, setTesting] = useState(false);

  const readOnly = activeEmail?.date_send || activeEmail?.status === "send";

  useEffect(() => {
    form.resetFields();
  }, [visible]);

  const uploadImage = (blob, filename) => {
    // rejecting with remove:true makes the editor drop the image and show the message
    const reject = (text) => Promise.reject({ message: text, remove: true });
    const nImages =
      (form.getFieldValue("content") || "").match(/<img/g)?.length || 0;

    if (!IMAGE_TYPES.includes(blob.type))
      return reject($t("Alleen jpg, png of gif plaatjes zijn toegestaan."));
    if (blob.size > MAX_IMAGE_BYTES)
      return reject($t("Het plaatje is te groot, maximaal 8MB."));
    if (nImages >= MAX_IMAGES)
      return reject($t("Maximaal ${n} plaatjes per email.", { n: MAX_IMAGES }));

    return uploadCampaignImage(activeEmail.id, blob, filename);
  };

  const changeFormat = (formatted) => {
    const current = form.getFieldValue("content");
    form.setFieldsValue({
      content_format: formatted ? HTML : TEXT,
      content: formatted ? textToHtml(current) : htmlToText(current),
    });
  };

  const sendTest = async () => {
    try {
      const values = await form.validateFields();
      setTesting(true);
      const success = await sendTestCampaign({
        reply_to_name: values.reply_to_name,
        reply_to: values.reply_to,
        subject: values.subject,
        content: values.content,
        content_format: values.content_format || HTML,
      });
      success && message.success($t("Test email verstuurd naar jezelf"));
    } catch (info) {
      message.error(
        $t("Data is incorrect, ${e} fouten gemeld", {
          e: info?.errorFields?.length,
        }),
      );
    }
    setTesting(false);
  };

  const saveForm = async (action) => {
    try {
      const values = await form.validateFields();

      // make sure at least groups or competencies are selected
      if (
        (!values.groups || values.groups.length === 0) &&
        (!values.competencies || values.competencies.length === 0)
      ) {
        message.error($t("Selecteer minimaal een groep of bevoegdheid"));
        return;
      }

      setLoading(action);

      const result = await saveEmail({
        campaign: {
          action,
          id: activeEmail.id,
          reply_to_name: values.reply_to_name,
          reply_to: values.reply_to,
          groups: values.groups,
          competencies: values.competencies,
          exclude_groups: values.exclude_groups,
          subject: values.subject,
          content: values.content,
          content_format: values.content_format || HTML,
        },
        files: values.attachments?.filter((f) => !!f.action),
      });

      if (result) {
        closeModal();
      }
      setLoading("");
    } catch (info) {
      console.error("error: ", { info });
      message.error(
        $t("Data is incorrect, ${e} fouten gemeld", {
          e: info?.errorFields?.length,
        }),
      );
      setLoading("");
    }
  };

  const triggerDelete = async () => {
    setLoading(DELETE);
    await saveEmail({ campaign: { id: activeEmail.id, action: DELETE } });
    closeModal();
    setLoading("");
  };

  const body = (
    <Form
      form={form}
      labelCol={{ span: 8 }}
      wrapperCol={{ span: 24 }}
      name="addEmail"
      layout="vertical"
      preserve={false}
      initialValues={activeEmail || {}}
      requiredMark={false}
    >
      <>
        <EmailCampaignStatus campaign={activeEmail} />{" "}
        {_parseDate(activeEmail.date_send)}
      </>

      <Form.Item
        style={{ marginTop: 12 }}
        label={
          <p>
            {$t("Ontvangers")}
            <HelpBubble
              content={$t(
                "Selecteer hier de groepen gebruikers die je wilt mailen. In de club settings kan je selecteren welke groepen zichtbaar zijn, onder het veld 'Groepen voor filter ledenlijst'. Je kan ook een los emailadres toevoegen.",
              )}
            />
          </p>
        }
        name="groups"
        rules={[
          { required: false },
          () => ({
            validator(_, values) {
              if (!values) {
                return Promise.resolve();
              }

              // existing group or correct email should validate properly
              const incorrectValues = [];
              values.map((v) => {
                if (
                  v !== "zweefvlieger" &&
                  groups.indexOf(v) === -1 &&
                  !validateEmail(v)
                ) {
                  incorrectValues.push(v);
                }
              });
              if (incorrectValues.length > 0) {
                return Promise.reject(
                  `"${incorrectValues.join(", ")}" ${$t(
                    "is incorrect, voeg groepen of valide emailaddressen toe.",
                  )} `,
                );
              }
              return Promise.resolve();
            },
          }),
        ]}
      >
        <Select mode="tags" disabled={readOnly}>
          <Select.Option key={-1} value="zweefvlieger">
            {$t("alle zweefvliegers")}
          </Select.Option>
          {groups &&
            groups.map((group, idx) => (
              <Select.Option key={idx} value={group.trim()}>
                {group_name_lookup[group] || group}
              </Select.Option>
            ))}
        </Select>
      </Form.Item>

      <Form.Item
        label={
          <p>
            {$t("Sluit groepen uit")}
            <HelpBubble
              content={$t(
                "Optionaal, sluit deze groepen uit van de ontvangers.",
              )}
            />
          </p>
        }
        name="exclude_groups"
        rules={[{ required: false }]}
      >
        <GroupFormSelect disabled={readOnly} />
      </Form.Item>

      <Form.Item
        label={
          <p>
            {$t("Bevoegdheden")}
            <HelpBubble
              content={$t(
                "Optioneel, filter op gebruikers die bevoegdheden hebben gehaald of toegewezen gekregen.",
              )}
            />
          </p>
        }
        name="competencies"
        rules={[{ required: false }]}
      >
        <CompetencySelector readOnly={readOnly} allowMultiple={true} />
      </Form.Item>

      <Form.Item
        label={
          <p>
            {$t("Naam verzender")}
            <HelpBubble
              content={$t(
                "Dit is de naam van waaruit de email verstuurd wordt.",
              )}
            />
          </p>
        }
        name="reply_to_name"
        rules={[{ required: false }]}
      >
        <Input type="text" disabled={readOnly} />
      </Form.Item>

      <Form.Item
        label={
          <p>
            {$t("Antwoord aan email")}
            <HelpBubble
              content={$t(
                "Dit is het emailadres van waaruit de mail verstuurd wordt, en waarop de ontvangers replyen.",
              )}
            />
          </p>
        }
        name="reply_to"
        rules={[{ required: true, type: "email" }]}
      >
        <Input type="email" disabled={readOnly} />
      </Form.Item>

      <Form.Item
        label={$t("Onderwerp")}
        name="subject"
        rules={[
          {
            required: true,
            message: $t("Maximaal ${c} characters", { c: 120 }),
            max: 1000,
          },
        ]}
      >
        <Input type="text" disabled={readOnly} />
      </Form.Item>

      <Form.Item>
        {activeEmail?.help && (
          <p className="small" style={{ marginTop: -6, marginBottom: 4 }}>
            {activeEmail.help}
          </p>
        )}
        <Form.Item name="content_format" initialValue={HTML} hidden>
          <Input />
        </Form.Item>
        {/* Form.Item injects value/onChange into its direct child, so Suspense must wrap the Form.Item */}
        <Suspense fallback={<Loading />}>
          <Form.Item
            label={$t("Tekst")}
            name="content"
            rules={[
              {
                required: true,
                message: $t("Voeg text toe, max ${c} characters", {
                  c: MAX_LENGTH[contentFormat],
                }),
                max: MAX_LENGTH[contentFormat],
              },
            ]}
          >
            {contentFormat === HTML ? (
              <RichTextEditor
                height={400}
                disabled={!!readOnly}
                menubar={false}
                plugins={EDITOR_PLUGINS}
                toolbar={EDITOR_TOOLBAR}
                configOverrides={EDITOR_OVERRIDES}
                imageUploadHandler={uploadImage}
                disabled={!!readOnly}
              />
            ) : (
              <Input.TextArea rows={8} disabled={readOnly} />
            )}
          </Form.Item>
        </Suspense>
        <Form.Item>
          <Switch
            size="small"
            disabled={!!readOnly}
            checked={contentFormat === HTML}
            onChange={changeFormat}
          />{" "}
          {$t("Met opmaak")}
        </Form.Item>
      </Form.Item>

      <Form.Item
        name="attachments"
        label={$t("Bijlage(s)")}
        rules={[{ required: false }, FileValidator]}
      >
        <FileInput readOnly={readOnly} />
      </Form.Item>
    </Form>
  );

  return (
    <Modal
      width={900}
      title={activeEmail?.id ? $t("Wijzig email") : $t("Voeg nieuw email toe")}
      open={visible}
      destroyOnClose
      confirmLoading={!!loadingAction}
      onCancel={() => {
        if (content === activeEmail.content) closeModal();
      }}
      footer={[
        activeEmail?.id && !activeEmail?.date_send && (
          <Popconfirm
            key="remove"
            placement="bottom"
            title={$t("Zeker weten dat je dit wilt verwijderen?")}
            onConfirm={triggerDelete}
            okText="Ja"
            cancelText="Nee"
          >
            <Button
              loading={loadingAction === DELETE}
              danger
              icon={<StopOutlined />}
            >
              {$t("verwijder")}
            </Button>
          </Popconfirm>
        ),
        <AnnuleerButton key="annuleren" onClick={closeModal} />,

        !readOnly && activeEmail?.status !== "send" && (
          <Button
            key="test"
            icon={<MailOutlined />}
            loading={testing}
            onClick={sendTest}
          >
            {$t("Verstuur test naar mezelf")}
          </Button>
        ),

        <SaveButton
          type={activeEmail?.id ? "default" : "primary"}
          key="o"
          onClick={() => saveForm(SAVE)}
          loading={loadingAction === SAVE}
        />,
      ]}
    >
      {readOnly ? (
        <Tabs
          items={[
            { key: "email", label: $t("Email"), children: body },
            {
              key: "stats",
              label: $t("Statistieken"),
              children: <EmailStats campaign={activeEmail} />,
            },
          ]}
        />
      ) : (
        body
      )}
    </Modal>
  );
};
